/* eslint-disable no-underscore-dangle */
import ErrorMessageProvider from '@ErrorMessageProvider';
import ScrollContainer from '@ScrollContainer';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import InputArea from '../InputArea/InputArea';
import {
  componentIdsIncludesTypes,
  getComponentIds,
  getPageErrorMessage,
  getVisiblePageComponents,
} from './page.helpers';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
import { selectPostSubmissionQuestionComponentIds } from '../../Helpers/strategyCollection.helpers';

const Page = () => {
  const { exitPhase, enterPhase } = useSelector((store) => store.transition);
  const { pages } = useSelector((store) => store.paging);
  const components = useSelector((store) => store.components);
  const { showCpcRecommendations, wasPreviousPageCpcCard } = useSelector(
    (store) => store.cpcCard
  );
  const { currentPage, slideDirection } = useSelector(
    (store) => store.navigation
  );
  const {
    iFrameId,
    isDynamicHeightEnabled,
    haveAllInitialMessagesArrived,
    maximumWidgetHeight,
  } = useSelector((store) => store.optionalFormConfig);
  const { requiredWidgetHeight } = useSelector((store) => store.dynamicHeight);
  const { dynamicNewsletterName } = useSelector((store) => store.newsletter);
  const { phoneNumberCountryCode, subcategoryId } = useSelector(
    (store) => store.formConfig
  );
  // The booking journey's questions are never drawn by the page: its own
  // screen asks them, and with no journey the page must skip cleanly.
  const journeyQuestionComponentIds = useSelector(
    selectPostSubmissionQuestionComponentIds,
    (a, b) => a.length === b.length && a.every((id, i) => id === b[i])
  );

  const [errorMessage, setErrorMessage] = useState(undefined);
  const pageComponentIds = pages[currentPage];
  const adjustedSlideDirection = pageComponentIds ? slideDirection : 'left';

  const errorMessageProviderExperimentName = useGetExperimentName({
    componentName: 'ErrorMessageProvider',
  });

  const isDynamicHeightWithinMaximumHeight =
    isDynamicHeightEnabled === true &&
    requiredWidgetHeight < maximumWidgetHeight;
  const haveAllInitialParentPageMessagesComeIn =
    haveAllInitialMessagesArrived === true;

  const isCpcCardShowingOrTransitioningOut = !!(
    isDynamicHeightEnabled &&
    (showCpcRecommendations || wasPreviousPageCpcCard)
  );

  const scrollDisabled =
    isDynamicHeightWithinMaximumHeight ||
    !haveAllInitialParentPageMessagesComeIn ||
    isCpcCardShowingOrTransitioningOut;

  const visiblePageComponents = getVisiblePageComponents(
    pageComponentIds,
    components,
    dynamicNewsletterName,
    phoneNumberCountryCode,
    subcategoryId,
    journeyQuestionComponentIds
  );

  const visiblePageComponentIds = getComponentIds({
    components: visiblePageComponents,
  });

  const noComponentsString = 'No components to display';

  if (visiblePageComponents.length > 0) {
    const pageError = getPageErrorMessage(visiblePageComponents);
    if (pageError !== errorMessage) {
      setErrorMessage(pageError);
    }
  }

  if (
    visiblePageComponents.length === 0 &&
    errorMessage !== noComponentsString
  ) {
    // if no components to display set error message
    setErrorMessage(noComponentsString);
  }

  useEffect(() => {
    if (
      !enterPhase &&
      currentPage !== undefined &&
      currentPage !== null &&
      !scrollDisabled
    ) {
      setTimeout(() => {
        const formContent = document.querySelector('#formContentContainer');
        if (formContent) {
          const scrollArea =
            formContent.children.length >= 2
              ? formContent.children[1]
              : undefined;
          if (scrollArea?.scroll && scrollArea?.scrollTop !== 0) {
            scrollArea.scroll(0, 0);
          }
        }
      }, 0);
    }
    /**
     * We are not dependent on the current page number, because it doesn't update after page change is completed, it is enterPhase that is updated to false when a page change is completed.
     *
     * currentPage should not be a dependency here as there could be a race between the enterPhase and the currentPage
     */
  }, [enterPhase]);

  useEffect(() => {
    const onContentMounting = window?.__private__?.onContentMounting;
    if (!isDynamicHeightEnabled && onContentMounting) {
      /**
       * Race condition resolution between the ScrollContainer recalculating the
       * scrollArea values (scrollHeight/clientHeight) whilst the inputArea of the
       * current page hasn't fully unmounted yet.
       * This callback function announces the beginning of the unmounting phase
       * of the inputArea followed by the mounting phase of the next page's
       * inputArea to the ScrollContainer, thus blocking any recalculations of
       * the scroll area.
       */
      onContentMounting();
    }
  }, [currentPage]);

  /**
   * default size of input area container 'md' and increased if required - but only under the conditions of being in a NEW entering phase
   */
  const [
    hasRadiosOrCheckboxesOnCurrentPage,
    setHasRadiosOrCheckboxesOnCurrentPage,
  ] = useState(
    !!componentIdsIncludesTypes({
      components,
      types: ['radios', 'checkboxes'],
      componentIds: visiblePageComponentIds,
    })
  );

  useEffect(() => {
    if (!exitPhase && enterPhase) {
      const mdOrLg = !!componentIdsIncludesTypes({
        components,
        types: ['radios', 'checkboxes'],
        componentIds: visiblePageComponentIds,
      });

      setHasRadiosOrCheckboxesOnCurrentPage(mdOrLg);
    }
  }, [exitPhase, enterPhase]);

  return (
    <ErrorMessageProvider
      errorMessage={errorMessage}
      experimentName={errorMessageProviderExperimentName}
    >
      {scrollDisabled ? (
        <div
          id="inputAreaParentContainer"
          style={{
            height: '0',
            flex: '1 1 auto',
            width: '100%',
            display: 'block',
            overflow: 'hidden',
            paddingLeft: '10px',
            paddingRight: '10px',
          }}
        >
          <InputArea
            components={components}
            hasRadiosOrCheckboxesOnCurrentPage={
              hasRadiosOrCheckboxesOnCurrentPage
            }
            currentPage={currentPage}
            iFrameId={iFrameId}
            pageComponentIds={pageComponentIds}
            slideDirection={adjustedSlideDirection}
            visiblePageComponents={visiblePageComponents}
          />
        </div>
      ) : (
        <ScrollContainer data-testid="scrollContainer">
          <InputArea
            components={components}
            currentPage={currentPage}
            hasRadiosOrCheckboxesOnCurrentPage={
              hasRadiosOrCheckboxesOnCurrentPage
            }
            iFrameId={iFrameId}
            pageComponentIds={pageComponentIds}
            slideDirection={adjustedSlideDirection}
            visiblePageComponents={visiblePageComponents}
          />
        </ScrollContainer>
      )}
    </ErrorMessageProvider>
  );
};

export default Page;
